import { formatCount, formatPercent } from '@/lib/format';
import type { ParamChange } from '@/model/diff';

/**
 * Renders structured changes as readable lines.
 *
 * This is the formatting half of the split `src/model/diff.ts` sets up: the model
 * returns raw numbers and a `kind`, and presentation happens here using the
 * existing formatters, so the model never learns about percent signs.
 *
 * A description list rather than a bulleted one: each row is a label paired with
 * a value, which is what `dl` is for, and it keeps these rows from registering as
 * list items alongside the timeline entries that contain them.
 */
export function ChangeList({ changes }: { changes: ParamChange[] }) {
  return (
    <dl className="change-list">
      {changes.map((change) => (
        <div key={change.path} className="change-list__item">
          <dt className="change-list__label">{change.label}</dt>
          <dd className="change-list__values">{describe(change)}</dd>
        </div>
      ))}
    </dl>
  );
}

function describe(change: ParamChange): string {
  switch (change.kind) {
    case 'added':
      return 'added';
    case 'removed':
      return 'removed';
    case 'name':
      return `${String(change.from)} → ${String(change.to)}`;
    default:
      return `${formatValue(change, change.from)} → ${formatValue(change, change.to)}`;
  }
}

function formatValue(change: ParamChange, value: number | string | null): string {
  if (value === null) return '—';
  if (typeof value === 'string') return value;
  if (change.kind === 'count') return formatCount(value);
  // Rates and shares are both 0..1 in the model.
  return formatPercent(value);
}
